<template>
  <div class="form-page-container">
    <PremiumPage
      title="Edit Album Metadata"
      subtitle="Update the basic identification and appearance of the album"
      showBack
      show-footer-buttons
      submit-label="Submit"
      :loading="loading"
      @cancel="$router.push('/societyadmin/gallery')"
      @submit="saveGalleryDetail"
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <v-row justify="center">
              <v-col cols="12">
                <div class="premium-field-label mb-6">Album Cover Image</div>
                
                <div class="profile-uploader-wrap">
                  <input
                    type="file"
                    @change="changeFile"
                    ref="fileInput"
                    hidden
                    accept="image/*"
                  />
                  
                  <div class="cover-preview shadow-lg" @click="openFileExplorer">
                    <v-img
                      v-if="previewImage || gallery.picture_url"
                      :src="previewImage || (API_BASE_URL + '/uploads/' + gallery.picture_url)"
                      class="preview-img"
                      contain
                    />
                    <div v-else class="preview-placeholder">
                      <v-icon size="48" color="#CBD5E1">mdi-image-plus-outline</v-icon>
                      <span class="mt-2">Click to Upload</span>
                    </div>
                    
                    <div class="edit-badge">
                      <v-icon small color="white">mdi-pencil</v-icon>
                    </div>
                  </div>

                  <div class="mt-4 text-tx3 text-caption font-weight-bold uppercase-label" :class="{ 'red--text': serverSideErrors.picture }">
                     {{ serverSideErrors.picture || "Recommended: JPG, PNG (Square)" }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row justify="center">
              <v-col cols="12">
                <PremiumInput
                  v-model="gallery.title"
                  label="Album Title *"
                  placeholder="e.g. Society Event 2024"
                  icon="mdi-folder-text-outline"
                  v-validate="'required|max:50'"
                  name="title"
                  :error="errors.first('title') || serverSideErrors.title"
                />
              </v-col>
            </v-row>
          </div>
        </v-form>
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";

export default {
  name: 'EditGallery',
  components: { PremiumPage, PremiumInput },
  data() {
    return {
      loading: false,
      serverSideErrors: {},
      previewImage: null,
      profile_pic: null,
      gallery: {
        id: null,
        title: "",
        picture_url: null,
      },
    };
  },
  computed: {
       ...mapState("theme", ["primaryColor", "buttonColor", "savedPrimaryColor", "isDark"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
  
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    getGalleryDetail() {
      this.$axios.get("societyadmin/gallery/" + this.$route.params.id)
        .then((response) => {
          if (response.data.status) {
            this.gallery = response.data.gallery;
          } else {
            this.showErr(response.data.message);
          }
        });
    },
    saveGalleryDetail() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.loading = true;
        let formData = new FormData();
        formData.append("title", this.gallery.title);
        // Note: The original code uses PUT with FormData which might need _method: 'PUT' if the server expects POST for multipart
        // or just regular PUT if it supports it. Multi-part PUT is tricky sometimes. 
        // Based on original code: _self.$axios.put("/societyadmin/gallery/" + this.gallery.id, formData)
        if (this.profile_pic) formData.append("picture", this.profile_pic);

        this.$axios.put("/societyadmin/gallery/" + this.gallery.id, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }).then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.setAlert({ showAlert: true, message: "Album updated successfully", variant: "green accent-4", timeout: 2000 });
            this.$router.push({ path: "/societyadmin/gallery" });
          } else {
            this.serverSideErrors = {};
            if (response.data.errors) response.data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
            else this.showErr(response.data.message || "An unexpected error occurred while updating the album.");
          }
        }).catch(() => {
          this.loading = false;
          this.showErr("Failed to connect to the server. Please check your connection and try again.");
        });
      });
    },
    openFileExplorer() { this.$refs.fileInput.click(); },
    changeFile(e) {
      const file = e.target.files[0];
      if (file) {
        this.profile_pic = file;
        const reader = new FileReader();
        reader.onload = (e) => { this.previewImage = e.target.result; };
        reader.readAsDataURL(file);
      }
    },
    showErr(msg) { this.setAlert({ showAlert: true, message: msg, variant: "red accent-3", timeout: 2000 }); },
  },
  mounted() {
    this.getGalleryDetail();
  },
};
</script>

<style scoped>
.form-page-container { padding: 24px; }

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.profile-uploader-wrap { display: flex; flex-direction: column; }

.cover-preview {
  width: 220px; height: 165px;
  background: var(--surface2);
  border: 2px dashed var(--border);
  border-radius: 20px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  display: flex; align-items: center; justify-content: center;
}
.cover-preview:hover { 
  border-color: var(--brand); 
  background: var(--surface3); 
  transform: translateY(-2px); 
}

.preview-img { width: 100%; height: 100%; object-fit: contain !important; }
.preview-placeholder { display: flex; flex-direction: column; align-items: center; color: var(--tx3); font-size: 13px; font-weight: 600; }

.edit-badge {
  position: absolute; bottom: 12px; right: 12px;
  width: 32px; height: 32px; border-radius: 10px;
  background: var(--brand) !important; 
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  z-index: 2;
}

.theme--dark .edit-badge {
  position: absolute; bottom: 12px; right: 12px;
  width: 32px; height: 32px; border-radius: 10px;
  background: #30384d !important; 
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  z-index: 2;
}

.uppercase-label { text-transform: uppercase; letter-spacing: 0.05em; font-size: 10px; }

.cancel-btn { color: var(--tx3); font-weight: 600; text-transform: none; }
.save-btn { border-radius: 12px !important; font-weight: 800; height: 46px !important; text-transform: none !important; }
</style>